import { useEffect, useState, type JSX } from "react";

import { Button, BasicModal } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { objectKeys } from "app/helpers/objects";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import { haveSameElements } from "app/pages/sales-intelligence/utils/arrays";
import type { ICountryObject } from "app/services/CountryService";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";

import { CountryRadioEnum } from "../../../types/common";

import {
    StyledModalContent,
    StyledButtonsContainer,
    StyledHeader,
    StyledTitle,
    StyledSubtitle,
    StyledMainContainer,
    StyledRadioContainer,
    StyledModalGlobal,
    CUSTOM_MODAL_STYLES,
} from "./styles";
import { handleCTAClick, TestCases } from "app/common/utils/handleGainAccessRedirect";
import { EHooksVersions } from "app/services/SwHook";

export type CountriesSelectionModalProps = {
    isOpened: boolean;
    className?: string;
    titleI18nKey?: string;
    subtitleI18nKey?: string;
    hasRadioOptions: boolean;
    availableRadioOptions?: Record<string, boolean>;
    selectedIds: readonly ICountryObject["id"][];
    onApply(selectedIds: readonly ICountryObject["id"][]): void;
    onCancel(): void;
    renderSelection(props: {
        selectedRadio: CountryRadioEnum;
        selectedIds: readonly ICountryObject["id"][];
        onSelect(countriesIds: readonly number[]): void;
    }): JSX.Element;
};

const CountriesSelectionModal = (props: CountriesSelectionModalProps) => {
    const {
        isOpened,
        selectedIds,
        hasRadioOptions,
        className = null,
        titleI18nKey = "si.components.countries_selection_modal.title",
        subtitleI18nKey = "si.components.countries_selection_modal.subtitle",
        onApply,
        onCancel,
        renderSelection,
        availableRadioOptions,
    } = props;
    const [selectedRadio, selectRadio] = useState(CountryRadioEnum.specific as string);
    const [selectedCountriesIds, setSelectedCountriesIds] = useState(selectedIds);

    /** Comparing previous with current selection */
    const isSelectionTheSame = haveSameElements(selectedIds, selectedCountriesIds);

    useEffect(() => {
        setSelectedCountriesIds(selectedIds);
    }, [selectedIds]);

    function handleCancel() {
        onCancel();
    }

    function handleApply() {
        onApply(selectedCountriesIds);
    }

    const handleRadioSelect = (id: string) => {
        if (availableRadioOptions?.[id]) {
            return handleCTAClick(EHooksVersions.V5, "WebCountry", TestCases.TRIAL_AFTER_TRIAL);
        }
        if (id === CountryRadioEnum.worldwide) {
            setSelectedCountriesIds([WORLDWIDE_COUNTRY_ID]);
        } else {
            setSelectedCountriesIds([]);
        }

        selectRadio(id);
    };

    return (
        <BasicModal
            isOpen={isOpened}
            closeIconConfig={{ show: false }}
            shouldCloseOnOverlayClick={false}
            customStyles={CUSTOM_MODAL_STYLES}
        >
            <StyledModalGlobal />
            <StyledModalContent
                className={className}
                data-automation="countries-selection-modal"
            >
                <StyledHeader>
                    <I18n
                        element={StyledTitle}
                        data-automation="countries-selection-modal.title-text"
                        translatable={{ key: titleI18nKey }}
                    />
                    <I18n
                        element={StyledSubtitle}
                        data-automation="countries-selection-modal.title.subtitle-text"
                        translatable={{ key: subtitleI18nKey }}
                    />
                </StyledHeader>
                <StyledMainContainer>
                    {hasRadioOptions ? (
                        <StyledRadioContainer
                            selected={selectedRadio}
                            onSelect={handleRadioSelect}
                            items={objectKeys(CountryRadioEnum).map((id) => {
                                return {
                                    id,
                                    text: translate(`si.components.countries_selection_modal.radio.${id}`),
                                    disabled: availableRadioOptions?.[id] as boolean,
                                };
                            })}
                        />
                    ) : null}
                    {renderSelection({
                        selectedRadio: selectedRadio as CountryRadioEnum,
                        selectedIds: selectedCountriesIds,
                        onSelect: setSelectedCountriesIds,
                    })}
                </StyledMainContainer>
                <StyledButtonsContainer>
                    <Button
                        type="flat"
                        onClick={handleCancel}
                        dataAutomation="countries-selection-modal-button-cancel"
                    >
                        {translate("si.common.button.cancel")}
                    </Button>
                    <Button
                        onClick={handleApply}
                        dataAutomation="countries-selection-modal-button-apply"
                        isDisabled={selectedCountriesIds.length === 0 || isSelectionTheSame}
                    >
                        {translate("si.common.button.apply")}
                    </Button>
                </StyledButtonsContainer>
            </StyledModalContent>
        </BasicModal>
    );
};

export default CountriesSelectionModal;
